import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { PageHeader, Panel, Spinner } from "../components/Panels.js";
import { nextMondayIso, thisMondayIso } from "../lib/format.js";

interface StaffRow {
  id: string;
  full_name: string;
  role: string;
  manager_name: string | null;
  business_unit_name: string | null;
}

interface GoalRow {
  id: string;
  staff_id: string;
  week_starting: string;
  objective: string;
  success_criteria: string | null;
  priority: "low" | "medium" | "high";
  status: string;
  display_order: number;
}

const EMPTY_GOAL = {
  objective: "",
  success_criteria: "",
  priority: "medium" as const,
};

export function GoalsPage() {
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thisWeek, setThisWeek] = useState<GoalRow[]>([]);
  const [nextWeek, setNextWeek] = useState<GoalRow[]>([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingWeek, setSavingWeek] = useState<string | null>(null);

  const monday = thisMondayIso();
  const nextMonday = nextMondayIso();

  useEffect(() => {
    api
      .get<{ staff: StaffRow[] }>("/staff")
      .then((r) => {
        setStaff(r.staff);
        if (r.staff[0]) setSelectedId(r.staff[0].id);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingGoals(true);
    Promise.all([
      api.get<{ goals: GoalRow[] }>(`/goals?staff_id=${selectedId}&week_starting=${monday}`),
      api.get<{ goals: GoalRow[] }>(`/goals?staff_id=${selectedId}&week_starting=${nextMonday}`),
    ])
      .then(([a, b]) => {
        setThisWeek(a.goals);
        setNextWeek(b.goals);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingGoals(false));
  }, [selectedId, monday, nextMonday]);

  const updateRow = (
    list: GoalRow[],
    setList: (v: GoalRow[]) => void,
    idx: number,
    patch: Partial<GoalRow>,
  ) => {
    const copy = [...list];
    copy[idx] = { ...copy[idx]!, ...patch } as GoalRow;
    setList(copy);
  };

  const saveWeek = async (week: string, rows: GoalRow[]) => {
    if (!selectedId) return;
    setSavingWeek(week);
    try {
      await api.put("/goals", {
        staff_id: selectedId,
        week_starting: week,
        goals: rows
          .filter((r) => r.objective.trim() !== "")
          .map((r) => ({
            objective: r.objective,
            success_criteria: r.success_criteria ?? null,
            priority: r.priority,
            display_order: r.display_order,
          })),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingWeek(null);
    }
  };

  if (error) return <div className="p-6 text-sm text-sentiment-overwhelmed">{error}</div>;
  if (!staff) return <div className="p-6"><Spinner /></div>;

  return (
    <>
      <PageHeader title="Goals" subtitle="Set up to 5 weekly objectives per staff member." />
      <div className="px-6 grid grid-cols-4 gap-4">
        <Panel title="Staff" className="col-span-1">
          <ul className="space-y-1">
            {staff.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                    selectedId === s.id ? "bg-bg-surface text-ink" : "text-ink-dim hover:bg-bg-panel"
                  }`}
                >
                  <div className="text-ink">{s.full_name}</div>
                  <div className="text-[11px] text-ink-dimmer">
                    {s.role}
                    {s.manager_name ? ` · ${s.manager_name}` : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="col-span-3 space-y-4">
          {loadingGoals ? (
            <Spinner />
          ) : (
            <>
              <GoalEditor
                title={`Current week — ${monday}`}
                rows={thisWeek}
                onChange={(rows) => setThisWeek(rows)}
                onSave={() => saveWeek(monday, thisWeek)}
                saving={savingWeek === monday}
                week={monday}
                staffId={selectedId}
                updateRow={updateRow}
              />
              <GoalEditor
                title={`Next week — ${nextMonday}`}
                rows={nextWeek}
                onChange={(rows) => setNextWeek(rows)}
                onSave={() => saveWeek(nextMonday, nextWeek)}
                saving={savingWeek === nextMonday}
                week={nextMonday}
                staffId={selectedId}
                updateRow={updateRow}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function GoalEditor({
  title,
  rows,
  onChange,
  onSave,
  saving,
  week,
  staffId,
  updateRow,
}: {
  title: string;
  rows: GoalRow[];
  onChange: (rows: GoalRow[]) => void;
  onSave: () => void;
  saving: boolean;
  week: string;
  staffId: string | null;
  updateRow: (
    list: GoalRow[],
    setList: (v: GoalRow[]) => void,
    idx: number,
    patch: Partial<GoalRow>,
  ) => void;
}) {
  const padded: GoalRow[] = [...rows];
  while (padded.length < 5) {
    padded.push({
      id: `new-${week}-${padded.length}`,
      staff_id: staffId ?? "",
      week_starting: week,
      status: "set",
      display_order: padded.length,
      ...EMPTY_GOAL,
    } as GoalRow);
  }

  return (
    <Panel
      title={title}
      action={
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !staffId}
          className="text-xs px-3 py-1 rounded-md bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      }
    >
      <div className="space-y-2">
        {padded.slice(0, 5).map((row, idx) => (
          <div
            key={row.id}
            className="grid grid-cols-12 gap-2 items-start"
          >
            <div className="col-span-1 text-xs text-ink-dimmer pt-2 pl-1">{idx + 1}.</div>
            <input
              className="col-span-6 bg-bg-base border border-bg-line2 rounded px-2 py-1 text-sm text-ink"
              placeholder="Objective"
              value={row.objective}
              onChange={(e) => updateRow(padded, onChange, idx, { objective: e.target.value })}
            />
            <input
              className="col-span-3 bg-bg-base border border-bg-line2 rounded px-2 py-1 text-sm text-ink"
              placeholder="Success criteria"
              value={row.success_criteria ?? ""}
              onChange={(e) =>
                updateRow(padded, onChange, idx, { success_criteria: e.target.value })
              }
            />
            <select
              className="col-span-2 bg-bg-base border border-bg-line2 rounded px-2 py-1 text-sm text-ink"
              value={row.priority}
              onChange={(e) =>
                updateRow(padded, onChange, idx, {
                  priority: e.target.value as GoalRow["priority"],
                })
              }
            >
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </div>
        ))}
      </div>
    </Panel>
  );
}
